/**
 * Search across every account the caller owns (TMMAPP-0133, TMMAPI-0165): `/me/search`, opened
 * from the app bar. One field, newest first, Load more to the last match. A row opens that
 * account's register with the same `q` and the line highlighted. Owner only.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { sentRegisterQuery, transactionsApi, type OwnerWideRegisterLine } from '@/api/transactions';
import { ErrorState } from '@/components/ErrorState';
import { SearchIcon } from '@/components/icons';
import { LiveRegion } from '@/components/LiveRegion';
import { MoneyText } from '@/components/MoneyText';
import { TabularText } from '@/components/TabularText';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { useMe } from '@/session/meHooks';
import { useDebouncedText } from '@/shared/useDebouncedText';
import { stringParam, useUrlState } from '@/shared/useUrlState';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { resolveTokens } from '@/theme/theme';

const URL = { q: stringParam() };
const PAGE_SIZE = 50;

function accountLabel(row: OwnerWideRegisterLine, t: TFunction): string {
  const last4 = row.account.last4;
  return last4 !== null && last4.length > 0
    ? t('search.accountLabel', { account: row.account.name, last4 })
    : row.account.name;
}

export function RegisterSearchPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const me = useMe();
  const guest = me.tier === 'GUEST';
  const subscriber = me.tier === 'SUBSCRIBER';
  const openSubscribe = useOpenSubscribe();
  const title = usePageTitle({ title: t('search.title') });
  const [{ q }, setUrl] = useUrlState(URL);
  const [text, setText] = useDebouncedText(q, (value) => setUrl({ q: value.trim() }));
  // GET /v1/me/register is subscriber-only. A guest sees the note; anyone else opens purchase.
  const sent = subscriber ? sentRegisterQuery(q) : '';
  const queryTooLong = text.trim().length > 100;
  const [tick, setTick] = useState(0);
  const key = `${sent}|${tick}`;
  const [page, setPage] = useState<{
    key: string;
    rows: OwnerWideRegisterLine[];
    nextCursor: string | null;
    count: number | null;
  } | null>(null);
  const [failed, setFailed] = useState<{ key: string; error: unknown } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);
  const focusId = useRef<number | null>(null);

  useEffect(() => {
    if (guest || subscriber) return;
    openSubscribe();
  }, [guest, subscriber, openSubscribe]);

  useEffect(() => {
    if (!subscriber || sent.length < 2) return undefined;
    const mine = ++seq.current;
    let live = true;
    void transactionsApi
      .searchAll({ q: sent, limit: PAGE_SIZE })
      .then((result) => {
        if (!live || seq.current !== mine) return;
        setPage({
          key,
          rows: result.items,
          nextCursor: result.nextCursor,
          count: result.totals?.count ?? null,
        });
        setFailed(null);
      })
      .catch((error: unknown) => {
        if (live && seq.current === mine) setFailed({ key, error });
      });
    return () => {
      live = false;
    };
  }, [subscriber, sent, key]);

  const ready = page !== null && page.key === key ? page : null;
  const error = failed !== null && failed.key === key ? failed : null;
  const rows = ready?.rows ?? [];

  useEffect(() => {
    const id = focusId.current;
    if (id === null) return;
    focusId.current = null;
    document.getElementById(`search-line-${id}`)?.focus();
  }, [ready]);

  async function handleLoadMore() {
    if (ready === null || ready.nextCursor === null || loadingMore) return;
    setLoadingMore(true);
    const start = ready.key;
    try {
      const next = await transactionsApi.searchAll({
        q: sent,
        cursor: ready.nextCursor,
        limit: PAGE_SIZE,
      });
      const first = next.items[0];
      if (first !== undefined) focusId.current = first.line.id;
      setPage((prev) =>
        prev !== null && prev.key === start
          ? {
              ...prev,
              rows: [...prev.rows, ...next.items],
              nextCursor: next.nextCursor,
            }
          : prev,
      );
    } catch (err) {
      setFailed({ key: start, error: err });
    } finally {
      setLoadingMore(false);
    }
  }

  function openLine(row: OwnerWideRegisterLine) {
    const params = new URLSearchParams({ q: sent, line: String(row.line.id) });
    void navigate(`/me/accounts/${row.account.id}?${params.toString()}`);
  }

  const count = ready?.count ?? rows.length;
  const live =
    ready === null || sent.length < 2
      ? ''
      : rows.length === 0
        ? t('search.empty', { q: sent })
        : t('search.matches', { shown: rows.length, count });

  return (
    <Box>
      <PageHeader title={title} />
      {guest ? (
        <Typography variant="body1">{t('search.guest')}</Typography>
      ) : (
        <Stack spacing={2}>
          <TextField
            type="search"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={t('search.placeholder')}
            fullWidth
            slotProps={{
              htmlInput: { 'aria-label': t('search.placeholder') },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Box component="span" aria-hidden sx={{ display: 'flex', color: tokens.text3 }}>
                      <SearchIcon />
                    </Box>
                  </InputAdornment>
                ),
              },
            }}
          />
          {queryTooLong ? (
            <Typography variant="body2" sx={{ color: tokens.text2 }}>
              {t('search.tooLong')}
            </Typography>
          ) : (
            sent.length < 2 &&
            text.trim().length > 0 && (
              <Typography variant="body2" sx={{ color: tokens.text2 }}>
                {t('search.tooShort')}
              </Typography>
            )
          )}
          <LiveRegion text={live} />
          {error !== null && (
            <ErrorState
              variant="section"
              error={error.error}
              what={t('errors.what.accountRegister')}
              onRetry={() => setTick((n) => n + 1)}
            />
          )}
          {error === null && sent.length >= 2 && ready === null && (
            <Stack spacing={1} aria-busy="true">
              <Skeleton height={40} />
              <Skeleton height={40} />
              <Skeleton height={40} />
            </Stack>
          )}
          {ready !== null && rows.length === 0 && (
            <Stack spacing={1}>
              <Typography variant="body1">{t('search.empty', { q: sent })}</Typography>
              <Box>
                <Button onClick={() => setUrl({ q: '' })}>{t('search.clear')}</Button>
              </Box>
            </Stack>
          )}
          {ready !== null && rows.length > 0 && (
            <Paper sx={{ p: 0, overflow: 'hidden' }}>
              <Typography
                variant="body2"
                sx={{ p: '12px 20px', color: tokens.textLabel('surface') }}
              >
                {t('search.matches', { shown: rows.length, count })}
              </Typography>
              {md ? (
                <TableContainer>
                  <Table size="small" aria-label={t('search.title')}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('accounts.colDate')}</TableCell>
                        <TableCell>{t('search.colAccount')}</TableCell>
                        <TableCell>{t('accounts.colDescription')}</TableCell>
                        <TableCell>{t('search.colPayee')}</TableCell>
                        <TableCell align="right">{t('accounts.colAmount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow
                          key={row.line.id}
                          id={`search-line-${row.line.id}`}
                          hover
                          tabIndex={0}
                          onClick={() => openLine(row)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openLine(row);
                            }
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <TabularText>{formatCivilDate(row.line.postedOn, locale)}</TabularText>
                          </TableCell>
                          <TableCell>{accountLabel(row, t)}</TableCell>
                          <TableCell>{row.line.payee}</TableCell>
                          <TableCell>{row.line.payeeRef?.name ?? '—'}</TableCell>
                          <TableCell align="right">
                            <MoneyText
                              minor={row.line.amountMinor}
                              currency={row.line.currency}
                              signed
                              tone="register"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {rows.map((row) => (
                    <Box component="li" key={row.line.id}>
                      <Box
                        component="button"
                        type="button"
                        id={`search-line-${row.line.id}`}
                        onClick={() => openLine(row)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          width: '100%',
                          textAlign: 'start',
                          font: 'inherit',
                          color: tokens.text,
                          background: 'transparent',
                          border: 'none',
                          borderTop: `1px solid ${tokens.line}`,
                          p: '14px 16px',
                          cursor: 'pointer',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}
                        >
                          <span>{row.line.payee}</span>
                          <MoneyText
                            minor={row.line.amountMinor}
                            currency={row.line.currency}
                            signed
                            tone="register"
                          />
                        </Box>
                        <Typography component="span" variant="caption" sx={{ color: tokens.text2 }}>
                          {formatCivilDate(row.line.postedOn, locale)} · {accountLabel(row, t)}
                          {row.line.payeeRef !== null ? ` · ${row.line.payeeRef.name}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {ready.nextCursor !== null && (
                <Stack direction="row" sx={{ justifyContent: 'center', p: 2 }}>
                  <Button
                    variant="outlined"
                    loading={loadingMore}
                    onClick={() => void handleLoadMore()}
                  >
                    {t('common.loadOlderLines')}
                  </Button>
                </Stack>
              )}
            </Paper>
          )}
        </Stack>
      )}
    </Box>
  );
}
